import React, { Fragment, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  GQLMerchantDeliveryType,
  GQLParty,
  GQLPlatformProducerOrder
} from '@jewelutions/core-crm-server-api-client-domain';

import { JwlTable, LoadingSpinner, ErrorComponent } from '../../../../components';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { ProducerNavigation } from '../../navigation/ProducerNavigation';
import { GQLAddressHelper } from '../../../../operations/helpers/GQLAddressHelper';

interface IProps {
  party: GQLParty;
}

const PlatformProducerOrdersList: React.FC<IProps> = ({ party }: IProps) => {
  const history = useHistory();
  const deliveryUnitId = ProducerNavigation.platformProducerOrderList.paramsFromPath(useRouteMatch())
    .deliveryUnitId;
  const [errorComponentState, setErrorComponentState] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState('');
  const { t: translate } = useTranslation();

  const {
    data: ordersData,
    loading: ordersLoading,
    error: ordersError,
    refetch: refetchOrders
  } = GQLOperationBuilder.deliveryUnit.getPlatformProducerOrdersByProducerAndDeliveryUnit({
    producerPartyId: party.id,
    producerDeliveryUnitId: deliveryUnitId
  });
  const reloadData = () => {
    refetchOrders().then();
  };

  if (ordersLoading) return <LoadingSpinner />;

  if (ordersError && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={ordersError?.stack}
        callback={reloadData}
        message={ordersError.message}
      />
    );

  const handleFilterChange = (event: any) => setFilteredOrders(event.target.value);

  const getOrderName = (order: GQLPlatformProducerOrder) =>
    order.delivery.deliveryType === GQLMerchantDeliveryType.Warehouse
      ? order.delivery.warehouseDelivery!.companyName
      : `${order.delivery.dropshipDelivery!.personFirstName} ${
          order.delivery.dropshipDelivery!.personLastName
        }`;

  const getOrderLocation = (order: GQLPlatformProducerOrder) =>
    order.delivery.deliveryType === GQLMerchantDeliveryType.Warehouse
      ? GQLAddressHelper.addressToString(order.delivery.warehouseDelivery!.address!)
      : GQLAddressHelper.addressToString(order.delivery.dropshipDelivery!.address!);

  const getOrderRows = () => {
    return GQLOperationBuilder.deliveryUnit
      .platformProducerOrderArrayFromResponse(ordersData)
      .filter(order => {
        return (
          getOrderName(order)
            .toLowerCase()
            .includes(filteredOrders.toLowerCase().trim()) ||
          getOrderLocation(order)
            .toLowerCase()
            .includes(filteredOrders.toLowerCase().trim())
        );
      })
      .map(order => {
        return {
          id: order.id,
          cells: [
            order.id,
            getOrderName(order),
            getOrderLocation(order),
            order.state,
            new Date(Date.parse(order.createdAt)).toLocaleString(),
            new Date(Date.parse(order.updatedAt)).toLocaleString()
          ]
        };
      });
  };

  const navigateToOrderDetails = (orderId: string) => {
    history.push(
      ProducerNavigation.platformProducerOrderDetail.pathFromParams({
        deliveryUnitId: deliveryUnitId,
        orderId: orderId
      })
    );
  };

  return (
    <Fragment>
      <JwlTable
        title="Delivery Unit producer orders List"
        description={`The list of all producer orders assigned to the currently selected delivery unit of ${party.name}`}
        columns={['ID', 'Delivery unit name', 'Location', 'State', 'Created At', 'Updated At']}
        rows={getOrderRows()}
        onFilterChange={handleFilterChange}
        onTableRowClick={navigateToOrderDetails}
      />
    </Fragment>
  );
};

export default PlatformProducerOrdersList;
